// Director mode (`?director=1`): the page's clocks stand still and advance only when told, so a
// recorder captures the same frames on every run.
//
//   recorder ──► step(frames, dt) ──┬─► page.tick(dt)      CSS transitions + performance.now
//                                   ├─► engine.renderOnce(dt)   camera springs and flights, time
//                                   │                          uniform, globe morph, labels
//                                   └─► wait for terrain chunks, redraw at dt = 0
//
// Nothing here reads the wall clock for what is drawn: the engine's rAF loop is stopped, CSS
// animations are held and moved by hand (director-page.ts), and `performance.now` is virtual.

import { lonLatToWorld, worldToLonLat } from '../core/grid.ts';
import type { LeaderId } from '../core/schema.ts';
import type { PanelId, Services } from '../services/api.ts';
import type { CoreMapView } from '../view/index.ts';
import type { DirectorApi, DirectorPose, Shot } from './harness-contract.ts';

/** The page-side effects a director drives, so tests need no DOM (director-page.ts is the real one). */
export interface DirectorPage {
  /** Advance every CSS transition and animation, and the virtual `performance.now`, by `dt` seconds. */
  tick(dt: number): void;
  /** Jump finite CSS animations to their end; infinite ones keep their phase. */
  finish(): void;
  /** Resolve once the UI has re-rendered after an action. */
  flush(): Promise<void>;
  /** Resolve after one macrotask, letting worker replies land. */
  idle(): Promise<void>;
  caption(text: string | null): void;
}

export interface DirectorDeps {
  view: CoreMapView;
  services: Services;
  page: DirectorPage;
  /** Apply a shot's state and pose (app-harness.ts), for `setShot`. */
  apply(shot: Shot): void;
}

export interface Director extends DirectorApi {
  /** `setShot` for a director page: apply the shot, then settle. */
  setShot(shot: Shot): Promise<void>;
}

const DEFAULT_DT = 1 / 60;

/** Give terrain chunks this long (real ms) to stream in after a frame; a stuck one must not hang a recording. */
const STREAM_TIMEOUT_MS = 10_000;

/** Frames drawn in a row with nothing pending before a settled shot counts as still. */
const CALM_FRAMES = 3;

/** A camera that has not come to rest after this many frames of `dt` is stuck. */
const SETTLE_MAX_FRAMES = 60 * 60;
const SETTLE_TIMEOUT_MS = 60_000;

export function createDirector(deps: DirectorDeps): Director {
  const { view, services, page, apply } = deps;
  const { engine, camera, terrain } = view.core;
  let running = false;
  let uiDirty = false;

  // The wall-clock loop would move the clocks between the recorder's steps.
  engine.stop();
  engine.time = 0;

  /** Redraw at dt = 0 (no clock moves) until the terrain chunks this camera wants have arrived. */
  async function stream(): Promise<void> {
    const deadline = Date.now() + STREAM_TIMEOUT_MS;
    while (terrain.stats().pending > 0 && Date.now() < deadline) {
      await page.idle();
      engine.renderOnce(0);
    }
  }

  async function flushUi(): Promise<void> {
    if (!uiDirty) {
      return;
    }
    uiDirty = false;
    await page.flush();
  }

  async function step(frames: number, dt = DEFAULT_DT): Promise<void> {
    if (!Number.isInteger(frames) || frames < 0) {
      throw new Error(`director.step: frames must be a whole number of 0 or more, got ${frames}`);
    }
    if (!(dt > 0)) {
      throw new Error(`director.step: dt must be above 0 seconds, got ${dt}`);
    }
    if (running) {
      throw new Error('director.step: a step is already running');
    }

    running = true;
    try {
      await flushUi();
      for (let i = 0; i < frames; i++) {
        page.tick(dt);
        engine.renderOnce(dt);
        await stream();
      }
    } finally {
      running = false;
    }
  }

  async function settle(): Promise<void> {
    await flushUi();

    // Run a flight or spring out at the frame rate, so the pose the shot describes is the one drawn.
    const deadline = Date.now() + SETTLE_TIMEOUT_MS;
    for (let i = 0; i < SETTLE_MAX_FRAMES && !camera.settled() && Date.now() < deadline; i++) {
      page.tick(DEFAULT_DT);
      engine.renderOnce(DEFAULT_DT);
    }

    // Then redraw without moving any clock until nothing is left to stream.
    let calm = 0;
    while (calm < CALM_FRAMES && Date.now() < deadline) {
      await page.idle();
      engine.renderOnce(0);
      calm = terrain.stats().pending > 0 ? 0 : calm + 1;
    }

    await flushUi();
    page.finish();
  }

  function flyTo(lon: number, lat: number, height: number, pitch: number, yaw: number, seconds: number): void {
    if (!(seconds > 0)) {
      throw new Error(`director.flyTo: seconds must be above 0, got ${seconds}`);
    }
    const at = lonLatToWorld(lon, lat);
    camera.flyTo(at.x, at.z, height, { seconds, pitch, yaw });
  }

  function pose(): DirectorPose {
    const p = camera.pose();
    const at = worldToLonLat(p.x, p.z);
    return { lon: at.lon, lat: at.lat, height: p.height, pitch: p.pitch, yaw: p.yaw };
  }

  /** An app action, then a UI flush before the next frame so the HUD shows its result. */
  function act(run: () => void): void {
    run();
    uiDirty = true;
  }

  return {
    step,
    settle,
    flyTo,
    pose,
    time: () => engine.time,
    async setShot(shot) {
      act(() => apply(shot));
      await settle();
    },
    setDate: (date) => act(() => services.actions.setDate(date)),
    async nextTurn() {
      await services.actions.nextTurn();
      uiDirty = true;
    },
    openPanel: (panel, arg) => act(() => services.actions.openPanel(panel as PanelId, arg)),
    chooseLeader: (id: LeaderId) => act(() => services.actions.chooseLeader(id)),
    setLens: (id) => act(() => services.actions.setLens(id)),
    setMode: (mode) => act(() => services.actions.setMode(mode)),
    caption: (text) => page.caption(text),
  };
}
